import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Milestone } from '@/components/celebrations/ListeningMilestoneModal';
import type { DailyListeningTimeData } from '@/services/listeningTime.service';
import {
  findCrossedHourMilestone,
  isPerfectWeek,
  getISOWeekKey,
} from '@/utils/milestones';
import { useBackendSettings } from './useBackendSettings';
import { settingsService } from '@/services/settings.service';

function parseCelebrated(raw: string | undefined): Set<string> {
  if (!raw) return new Set();
  try {
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function milestoneKey(m: Milestone): string {
  return m.type === 'hours' ? `${m.hours}h` : m.weekKey;
}

export function useCelebrations() {
  const { settings } = useBackendSettings();
  const queryClient = useQueryClient();
  const [activeCelebration, setActiveCelebration] = useState<Milestone | null>(null);
  // Track milestones celebrated in this session (before server confirms) to prevent double-fire
  const pendingRef = useRef<Set<string>>(new Set());

  const getCelebrated = useCallback((): Set<string> => {
    return parseCelebrated(settings['celebrated_milestones']);
  }, [settings]);

  const checkAndMaybeSetMilestone = useCallback(
    (
      prevTotalSeconds: number,
      newTotalSeconds: number,
      dailyReport: DailyListeningTimeData[],
    ) => {
      const celebrated = getCelebrated();

      // Check hours milestone
      const crossedHours = findCrossedHourMilestone(prevTotalSeconds, newTotalSeconds);
      if (crossedHours !== null) {
        const key = `${crossedHours}h`;
        if (!celebrated.has(key) && !pendingRef.current.has(key)) {
          pendingRef.current.add(key);
          setActiveCelebration({ type: 'hours', hours: crossedHours });
          // Only fire one celebration at a time. If multiple milestones are crossed
          // simultaneously (e.g. data import), the lowest is shown first.
          // Subsequent ones will be offered on future calls once this one is dismissed.
          return;
        }
      }

      // Check perfect week
      const now = new Date();
      const todayStr = now.toISOString().slice(0, 10);
      const weekKey = getISOWeekKey(now);
      if (
        !celebrated.has(weekKey) &&
        !pendingRef.current.has(weekKey) &&
        isPerfectWeek(dailyReport, todayStr)
      ) {
        pendingRef.current.add(weekKey);
        setActiveCelebration({ type: 'week', weekKey });
      }
    },
    [getCelebrated],
  );

  // Note: getCelebrated() reads settings at call time. If a previous saveSettings
  // call is still inflight when dismiss is called, this write merges against a
  // potentially stale snapshot. In practice only one celebration fires at a time,
  // so this race is extremely unlikely. A future improvement would clear pendingRef
  // entries on confirmed save success/failure.
  const dismissCelebration = useCallback(() => {
    if (!activeCelebration) return;
    const key = milestoneKey(activeCelebration);
    const celebrated = getCelebrated();
    celebrated.add(key);
    // Call service directly to avoid the generic "Settings saved" toast from useBackendSettings
    settingsService.saveSettings({ celebrated_milestones: JSON.stringify([...celebrated]) })
      .then(() => {
        // Invalidate so other consumers of settings stay in sync
        queryClient.invalidateQueries({ queryKey: ['user-backend-settings'] });
      })
      .catch((err: Error) => {
        console.error('Failed to persist celebrated milestone:', err);
      });
    setActiveCelebration(null);
  }, [activeCelebration, getCelebrated, queryClient]);

  return { activeCelebration, checkAndMaybeSetMilestone, dismissCelebration };
}
